/**
 * Constants and utility functions for DeleteMediaKitModal
 */

// Route patterns for media kits navigation
export const MEDIA_KITS_ROUTES = {
  MAIN: '/media-kits',
  CREATOR_MEDIA_KITS: '/creator-media-kits',
  CREATOR_PATTERN: /^\/t\/[^/]+\/media-kits$/,
  BUILDER_PATTERN: /^\/media-kits\/[^/]+\/builder$/,
} as const;

/**
 * Checks if a path is a creator's media kits page
 * @param path - The path to check
 * @returns true if the path matches a creator's media kits page pattern
 */
export const isCreatorMediaKitsPage = (path: string): boolean =>
  MEDIA_KITS_ROUTES.CREATOR_PATTERN.test(path) ||
  path === MEDIA_KITS_ROUTES.CREATOR_MEDIA_KITS;

/**
 * Checks if a path is the main media kits page
 * @param path - The path to check
 * @returns true if the path is the main media kits page
 */
export const isMainMediaKitsPage = (path: string): boolean =>
  path === MEDIA_KITS_ROUTES.MAIN;

/**
 * Checks if a path is a media kit builder page
 * @param path - The path to check
 * @returns true if the path matches a builder page pattern
 */
export const isBuilderPage = (path: string): boolean =>
  MEDIA_KITS_ROUTES.BUILDER_PATTERN.test(path);

/**
 * Finds the most recent creator's media kits page in navigation history
 * @param history - Array of navigation history paths
 * @returns The creator's media kits page path or null if not found
 */
export const findCreatorPageInHistory = (history: string[]): string | null => {
  if (history.length <= 1) return null;

  // Look backwards through history (excluding current page)
  for (let i = history.length - 2; i >= 0; i--) {
    const page = history[i];
    if (isCreatorMediaKitsPage(page)) {
      return page;
    }
  }
  return null;
};

/**
 * Determines the redirect path after deleting a media kit
 * @param history - Array of navigation history paths
 * @returns The path to redirect to after deletion
 */
export const getDeleteRedirectPath = (history: string[]): string => {
  // Early return for empty history
  if (!history.length) {
    return MEDIA_KITS_ROUTES.MAIN;
  }

  const currentPage = history[history.length - 1];

  // If we're already on a media kits page (creator or main), stay there
  if (isCreatorMediaKitsPage(currentPage) || isMainMediaKitsPage(currentPage)) {
    return currentPage;
  }

  // If we're in the builder, look for a creator's media kits page in history
  if (isBuilderPage(currentPage)) {
    return findCreatorPageInHistory(history) ?? MEDIA_KITS_ROUTES.MAIN;
  }

  // Fallback to main media kits page
  return MEDIA_KITS_ROUTES.MAIN;
};
